import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth, db } from "../../firebase/config";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
  orderBy,
  onSnapshot,
  getDoc,
} from "firebase/firestore";
import { useAuth } from "../../context/AuthContext";
import useSaveFcmToken from "../../hooks/useSaveFcmToken";
import { updateDeliveryStatusWithNotification } from "../../utils/deliveryNotifications";

const PartnerDashboard = () => {
  const { user, role, refreshUserToken } = useAuth();
  const navigate = useNavigate();
  const [partnerData, setPartnerData] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [slot, setSlot] = useState("Morning");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("deliveries"); // "deliveries" or "bonus" or "delivered"
  const [earnings, setEarnings] = useState({
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    totalDelivered: 0,
    totalPending: 0,
    totalNotDelivered: 0,
    totalPickedUp: 0
  });
  const [bonusSettings, setBonusSettings] = useState({ baseEarning: 10, bonusPerStreak: 50, minDeliveriesForBonus: 5 });

  useSaveFcmToken();

  // ✅ Role check and fallback handling
  useEffect(() => {
    if (user && !role) {
      console.log("⚠️ User logged in but role is undefined. Attempting refresh...");
      refreshUserToken();
    }
  }, [user, role, refreshUserToken]);

  // ✅ Only run if user exists
  useEffect(() => {
    if (!user) return;
    const stored = localStorage.getItem("deliveryPartner");
    if (stored) {
      setPartnerData(JSON.parse(stored));
    }
  }, [user]);

  // Fetch bonus settings in real-time
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "app_settings", "bonus_config"), (docSnap) => {
      if (docSnap.exists()) {
        setBonusSettings(docSnap.data());
      }
    });
    return () => unsub();
  }, []);

  const fetchDeliveries = async () => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const q = query(
        collection(db, "deliveries"),
        where("assignedPartnerId", "==", user.uid),
        where("date", "==", date),
        where("slot", "==", slot),
        orderBy("date")
      );
      const snap = await getDocs(q);
      let data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      if (statusFilter !== "all") {
        data = data.filter((d) => d.status === statusFilter);
      }
      setDeliveries(data);
    } catch (error) {
      console.error("Error fetching deliveries:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEarnings = async () => {
    if (!user?.uid) return;
    
    try {
      const today = new Date().toISOString().slice(0, 10);
      const startOfWeek = new Date();
      startOfWeek.setDate(startOfWeek.getDate() - 7);
      const startOfMonth = new Date();
      startOfMonth.setDate(1);

      // Fetch all partner's deliveries
      const q = query(
        collection(db, "deliveries"),
        where("assignedPartnerId", "==", user.uid),
        where("date", "==", date),
        where("slot", "==", slot)
      );
      
      const snap = await getDocs(q);
      const allDeliveries = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));

      // Use dynamic rate from bonusSettings
      const deliveryRate = bonusSettings.baseEarning || 10;
      
      const todayDeliveries = allDeliveries.filter(d => d.date === today && d.status === "delivered");
      const weekDeliveries = allDeliveries.filter(d => 
        d.date >= startOfWeek.toISOString().slice(0, 10) && d.status === "delivered"
      );
      const monthDeliveries = allDeliveries.filter(d => 
        d.date >= startOfMonth.toISOString().slice(0, 10) && d.status === "delivered"
      );

      const totalDelivered = allDeliveries.filter(d => d.status === "delivered").length;
      const totalPending = allDeliveries.filter(d => d.status === "pending").length;
      const totalNotDelivered = allDeliveries.filter(d => d.status === "not_delivered").length;
      const totalPickedUp = allDeliveries.filter(d => d.status === "picked_up").length;

      setEarnings({
        today: todayDeliveries.length * deliveryRate,
        thisWeek: weekDeliveries.length * deliveryRate,
        thisMonth: monthDeliveries.length * deliveryRate,
        totalDelivered,
        totalPending,
        totalNotDelivered,
        totalPickedUp
      });
    } catch (error) {
      console.error("Error fetching earnings:", error);
    }
  };

  useEffect(() => {
    fetchDeliveries();
    fetchEarnings();
  }, [user, date, slot, statusFilter]);

  const updateDeliveryStatus = async (id, newStatus) => {
    try {
      // Find the delivery to get customer info
      const delivery = deliveries.find(d => d.id === id);
      const customerName = delivery?.customerName || delivery?.userName || "Customer";
      const userId = delivery?.userId;
      
      // Use enhanced update function with notification
      const success = await updateDeliveryStatusWithNotification(
        id, 
        newStatus, 
        customerName, 
        userId
      );
      
      if (success) {
        setDeliveries((prev) =>
          prev.map((d) => (d.id === id ? { ...d, status: newStatus } : d))
        );
        // Refresh earnings after status update
        fetchEarnings();
      } else {
        alert("Failed to update status. Try again.");
      }
    } catch (err) {
      console.error("Error updating delivery status:", err);
      alert("Failed to update status. Try again.");
    }
  };

  const handleLogout = async () => {
    try {
      localStorage.removeItem("deliveryPartner");
      await signOut(auth);
      navigate("/delivery-partner/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-green-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-green-700 mb-4">
            🚚 Delivery Partner Dashboard
          </h1>
          <p className="text-gray-600 mb-4">
            Please log in to access your dashboard
          </p>
          <button
            onClick={() => navigate("/delivery-partner/login")}
            className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700"
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-50 p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-green-700">Partner Dashboard</h1>
          <p className="text-gray-600">Welcome, {user?.displayName || 'Partner'}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleLogout}
            className="text-sm bg-red-100 text-red-700 px-4 py-2 rounded hover:bg-red-200"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-green-100">
          <div className="text-2xl font-bold text-green-600">₹{earnings.today}</div>
          <div className="text-sm text-gray-600">Today's Earnings</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-green-100">
          <div className="text-2xl font-bold text-blue-600">₹{earnings.thisWeek}</div>
          <div className="text-sm text-gray-600">This Week</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-green-100">
          <div className="text-2xl font-bold text-orange-600">{earnings.totalDelivered}</div>
          <div className="text-sm text-gray-600">Total Delivered</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border border-green-100">
          <div className="text-2xl font-bold text-purple-600">{deliveries.length}</div>
          <div className="text-sm text-gray-600">Today's Assignments</div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab("deliveries")}
          className={`px-4 py-2 font-medium ${
            activeTab === "deliveries"
              ? "text-green-600 border-b-2 border-green-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          📦 Assigned Deliveries
        </button>
        <button
          onClick={() => setActiveTab("delivered")}
          className={`px-4 py-2 font-medium ${
            activeTab === "delivered"
              ? "text-green-600 border-b-2 border-green-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          ✅ Delivered
        </button>
        <button
          onClick={() => setActiveTab("bonus")}
          className={`px-4 py-2 font-medium ${
            activeTab === "bonus"
              ? "text-green-600 border-b-2 border-green-600"
              : "text-gray-500 hover:text-gray-700"
          }`}
        >
          🪙 Earnings & Bonus
        </button>
      </div>

      {/* Deliveries Tab */}
      {activeTab === "deliveries" && (
        <div>
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6">
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="border px-3 py-2 rounded-lg"
            />
            <select
              value={slot}
              onChange={(e) => setSlot(e.target.value)}
              className="border px-3 py-2 rounded-lg"
            >
              <option value="Morning">Morning</option>
              <option value="Evening">Evening</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border px-3 py-2 rounded-lg"
            >
              <option value="all">All</option>
              <option value="pending">⏳ Pending</option>
              <option value="delivered">✅ Delivered</option>
              <option value="not_delivered">❌ Not Delivered</option>
            </select>
          </div>

          {/* Deliveries List */}
          {loading ? (
            <p className="text-green-600">Loading deliveries...</p>
          ) : deliveries.filter(d => d.status !== 'delivered').length === 0 ? (
            <div className="bg-white p-8 rounded-xl shadow-sm border border-green-100 text-center">
              <div className="text-4xl mb-4">📦</div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No Assigned Deliveries</h3>
              <p className="text-gray-600">No pending deliveries for the selected date and slot.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {deliveries.filter(d => d.status !== 'delivered').map((delivery) => (
                <div
                  key={delivery.id}
                  className="bg-white p-4 rounded-xl shadow-sm border border-green-100"
                >
                  <div className="flex justify-between">
                    <div>
                      <p className="font-semibold text-green-700">
                        {delivery.customerName || delivery.userId}
                      </p>
                      <p className="text-gray-600">{delivery.address}</p>
                      <p className="text-gray-600">
                        📞 {delivery.customerPhone && (
                          <a href={`tel:${delivery.customerPhone}`} className="text-green-700 font-bold hover:underline">{delivery.customerPhone}</a>
                        )}
                      </p>
                      <p className="text-sm text-gray-500">📦 {delivery.planName || delivery.plan}</p>
                    </div>
                    <div className="text-right">
                      <a
                        href={
                          delivery.lat && delivery.lng
                            ? `https://www.google.com/maps/dir/?api=1&destination=${delivery.lat},${delivery.lng}`
                            : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(delivery.address)}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline text-sm"
                      >
                        🗺️ View in Maps
                      </a>
                      <div className="mt-2">
                        <span className={`px-2 py-1 rounded text-xs ${
                          delivery.status === 'delivered' ? 'bg-green-100 text-green-800' :
                          delivery.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          delivery.status === 'picked_up' ? 'bg-blue-100 text-blue-800' :
                          delivery.status === 'not_delivered' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {delivery.status}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <label className="text-sm text-gray-700">Update Status:</label>
                    <select
                      value={delivery.status}
                      onChange={(e) =>
                        updateDeliveryStatus(delivery.id, e.target.value)
                      }
                      className="border px-2 py-1 rounded-lg"
                    >
                      <option value="picked_up">📦 Picked up</option>
                      <option value="pending">⏳ Pending</option>
                      <option value="delivered">✅ Delivered</option>
                      <option value="not_delivered">❌ Not Delivered</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Delivered Tab */}
      {activeTab === "delivered" && (
        <div>
          {loading ? (
            <p className="text-green-600">Loading deliveries...</p>
          ) : deliveries.filter(d => d.status === 'delivered').length === 0 ? (
            <div className="bg-white p-8 rounded-xl shadow-sm border border-green-100 text-center">
              <div className="text-4xl mb-4">✅</div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">No Delivered Orders</h3>
              <p className="text-gray-600">No delivered orders for the selected date and slot.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {deliveries.filter(d => d.status === 'delivered').map((delivery) => (
                <div key={delivery.id} className="bg-white p-4 rounded-xl shadow-sm border border-green-100">
                  <p className="font-semibold text-green-700">{delivery.customerName || delivery.userId}</p>
                  <p className="text-sm text-gray-500">📦 {delivery.planName || delivery.plan}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Bonus/Earnings Tab */}
      {activeTab === "bonus" && (
        <div className="space-y-6">
          {/* Earnings Overview */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-green-100">
            <h2 className="text-xl font-bold text-green-700 mb-4">💰 Earnings Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-3xl font-bold text-green-600">₹{earnings.today}</div>
                <div className="text-sm text-gray-600">Today's Earnings</div>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-3xl font-bold text-blue-600">₹{earnings.thisWeek}</div>
                <div className="text-sm text-gray-600">This Week</div>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <div className="text-3xl font-bold text-orange-600">₹{earnings.thisMonth}</div>
                <div className="text-sm text-gray-600">This Month</div>
              </div>
            </div>
          </div>

          {/* Delivery Stats */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-green-100">
            <h2 className="text-xl font-bold text-green-700 mb-4">📊 Delivery Statistics</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="text-center p-4 bg-green-50 rounded-lg">
                <div className="text-3xl font-bold text-green-600">{earnings.totalDelivered}</div>
                <div className="text-sm text-gray-600">Total Delivered</div>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-3xl font-bold text-red-600">{earnings.totalNotDelivered}</div>
                <div className="text-sm text-gray-600">Total Not Delivered</div>
              </div>
            </div>
          </div>

          {/* Bonus Information */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-green-100">
            <h2 className="text-xl font-bold text-green-700 mb-4">🎯 Bonus Information</h2>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                <span className="font-medium">Per Delivery Rate:</span>
                <span className="font-bold text-green-600">₹{bonusSettings.baseEarning}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                <span className="font-medium">Bonus per Streak ({bonusSettings.minDeliveriesForBonus}+ deliveries):</span>
                <span className="font-bold text-blue-600">₹{bonusSettings.bonusPerStreak}</span>
              </div>
            </div>
          </div>

          {/* Payment Schedule */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-green-100">
            <h2 className="text-xl font-bold text-green-700 mb-4">📅 Payment Schedule</h2>
            <div className="space-y-2 text-gray-600">
              <p>• Daily earnings are calculated based on completed deliveries</p>
              <p>• Weekly payments are processed every Sunday</p>
              <p>• Monthly bonuses are paid on the 1st of each month</p>
              <p>• Contact admin for any payment-related queries</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnerDashboard;
